import { Injectable } from '@angular/core';
import { forkJoin, of, Subject } from 'rxjs';
import { BehaviorSubject } from 'rxjs/internal/BehaviorSubject';
import { catchError, filter, map, switchMap, takeUntil, tap } from 'rxjs/operators';
import { GlobalConstants } from 'src/app/common/constants/global.constant';
import { environment } from 'src/environments/environment';
import { manufacturerMapping, manufacturerNewMapping } from 'src/app/common/constants/qrCodeMapping';
import { QrCodeService } from 'src/app/core/services/qr-input-service/qr-input.service';
import { HttpMethodsService } from 'src/app/common/utility/http-methods.service';

@Injectable({
  providedIn: 'root'
})
export class PromoContentService {
  promoData: any[] = [];
  promoTileData: any[] = [];
  noExpiry: boolean = false;
  promoResult: any;

  private selectedPromoIndex = new BehaviorSubject<any>(null);
  getSelectedPromoIndex = this.selectedPromoIndex.asObservable();
  private promoDataLength = new BehaviorSubject<any>(0);
  getPromoDataLength = this.promoDataLength.asObservable();
  private ngUnsubscribe = new Subject<boolean>();

  constructor(private httpService: HttpMethodsService, private qrInputService: QrCodeService) { }

  getPromoDetails(onComplete: () => void) {
    const qrURL = environment.deviceDetailsUrl + this.qrInputService?.getDeviceid();
    const userID = this.qrInputService?.qrInput?.UserId;
    const proc_badge = this.qrInputService?.qrInput?.ProcessorBadge;
    const cpuFamily = GlobalConstants.PANTHER_LAKE_CPU_FAMILY_MAPPING[proc_badge];
    const cpuGeneration = (GlobalConstants.PANTHER_LAKE_BADGES.includes(proc_badge) || GlobalConstants.PANTHER_LAKE_12XE_BADGES.includes(proc_badge)) ? GlobalConstants.THREE.toString() : '';
    const systemManufacturerCode = this.qrInputService?.qrInput?.SystemManufacturer;
    const systemManufacturer = (systemManufacturerCode && systemManufacturerCode?.length > 1) ? manufacturerMapping[systemManufacturerCode] : manufacturerNewMapping[systemManufacturerCode];
    let deviceType: string;
    let skuId: string;
    let promoIds: any[];

    this.promoResult = this.httpService.get(qrURL).pipe(takeUntil(this.ngUnsubscribe),
      //AI generated: copilot generated code start- Call the promo details API by filtering promo ID's from device details API response
      tap((res: any) => {
        const ftag = res?.device?.factTags;
        skuId = res?.device?.skuID;
        const settings = res?.device?.settings;

        if (ftag) {
          try {
            const parsedFtag = typeof ftag === 'string' ? JSON.parse(ftag) : ftag;
            const deviceTypeArray = parsedFtag.find((data: any) => data.name === 'device_type');
            deviceType = GlobalConstants.PROMO_2_IN_1_DEVICES.includes(deviceTypeArray?.values?.device) ? GlobalConstants.DEVICE_TYPE_2_IN_1 : deviceTypeArray?.values?.device;
          } catch (e) {
            onComplete();
            console.error('Error parsing ftag', e);
          }
        }
        if (skuId) { // Filtering promoIDs from selected sku
          try {
            const parsedSetting = typeof ftag === 'string' ? JSON.parse(settings) : settings;
            const selectedSkuDetails = parsedSetting?.otherSettings?.skuList.find((item: any) => item.skuCode === skuId);
            promoIds = selectedSkuDetails?.promotionalContentIDs;
          } catch (e) {
            onComplete();
            console.error('Error parsing sku data', e);
          }
        }
      }),
      filter((res: any) => { // Check for promo availability
        if (skuId) {
          return promoIds.filter(Boolean).length > 0; // Filter out invalid (null, undefined, '', 0, false) and return valid promoIDs
        } else {
          return (
            Array.isArray(res?.device?.promotionalContents) &&
            res.device.promotionalContents.length > 0
          );
        }
      }),
      map((res: any) => { // Get list of IDs
        if (skuId) {
          return promoIds.filter(Boolean);
        } else {
          return res.device.promotionalContents.map((item: any) => item.promotionalContentId);
        }
      }),
      switchMap((ids: string[]) => // Calling promo details API for all IDs
        forkJoin(ids.map(id =>
          this.httpService.get(environment.promoDetailsUrl + userID + GlobalConstants.PROMO_URL2_PART1 + userID + GlobalConstants.PROMO_URL2_PART2 + id).pipe(
            catchError(err => {
              console.error(`Error for ID ${id}`, err);
              return of(null); // return safe fallback instead of breaking forkJoin
            })
          )

        ))
      ),
      map((responses: any[]) =>
        responses
          .filter(r => r !== null)
          .map(r => r.promotionalContent)
          .map(item => ({
            ...item,
            configurations:
              typeof item.configurations === 'string'
                ? JSON.parse(item.configurations)
                : item.configurations
          }))
          .filter(item =>
            this.isPublishExpiryValid(
              item?.configurations?.publishTime,
              item?.configurations?.expireTime
            )
          )
          .filter(item =>
            this.checkFormFactorAvailability(item.configurations.formFactor, deviceType, systemManufacturer, cpuFamily, cpuGeneration)
          )
      )
      //copilot generated code end
    ).subscribe({
      next: (promoDetailsArray) => {
        this.promoData = promoDetailsArray;
        this.setPromoDataLength(promoDetailsArray.length);
        onComplete();
      },
      error: (error) => {
        console.error('API error', error);
        onComplete();
      },
      complete: () => {
        onComplete();
      }
    });
  }

  setSelectedPromoIndex(index: any): void {
    this.selectedPromoIndex.next(index);
  }

  setPromoDataLength(index: any): void {
    this.promoDataLength.next(index);
  }

  isPublishExpiryValid(publishDate: string, expiryDate: string) {
    let validPublishEXpiry = false;
    this.noExpiry = this.dateExpiryCheck(expiryDate);
    if (expiryDate === '' && publishDate === '') {
      validPublishEXpiry = true;
    } else if (expiryDate === '' && publishDate !== '') {
      if (new Date(publishDate) < new Date()) {
        validPublishEXpiry = true;
      }
    } else if (expiryDate !== '' && publishDate === '') {
      if (this.noExpiry) {
        validPublishEXpiry = true;
      }
    } else if (expiryDate !== '' && publishDate !== '') {
      if ((new Date(publishDate) < new Date()) && this.noExpiry) {
        validPublishEXpiry = true;
      }
    }
    return validPublishEXpiry;
  }

  dateExpiryCheck(expireTime: string) {
    let expiryCheck = false;
    if (expireTime === 'Never' || (new Date() <= new Date(expireTime))) {
      expiryCheck = true;
    }
    return expiryCheck;
  }

  // check if formfactor conditions are matching to show Content group
  public checkFormFactorAvailability(formFactorObject: any, deviceType: string, manufacturer: string, cpuFamily: string, cpuGeneration: string) {
    // check if allDevices=true
    if (formFactorObject?.allDevices) {
      return true;
    } else {
      /* Checking if array of(devices/manufatcurer/generation/processor) is empty, it means selectAll is true 
      for respective array and show that CG for selectAll and respective combinations */
      if (formFactorObject?.devices?.length > 0 && !formFactorObject?.devices?.includes(deviceType)) {
        return false;
      }
      if (formFactorObject?.processors?.length > 0 && !formFactorObject?.processors?.includes(cpuFamily)) {
        return false;
      }
      if (formFactorObject?.generations?.length > 0 && !formFactorObject?.generations?.includes(cpuGeneration)) {
        return false;
      }
      if (formFactorObject?.manufacturer?.length > 0) {
        const isOthersSelected = formFactorObject?.manufacturer?.includes(GlobalConstants.UNKNOWN);
        if (formFactorObject?.manufacturer?.includes(manufacturer)) {
          return true;
        } else if (!formFactorObject?.manufacturer?.includes(manufacturer) && isOthersSelected) {
          return true;
        } else {
          return false;
        }
      }
      return true;
    }
  }

    ngOnDestroy(): void {
    this.ngUnsubscribe.next(true);
    this.ngUnsubscribe.complete();
  }

}